import { createStore } from 'vuex'
import { fetchUserData, authenticateWithGoogle, authenticateWithSpotify, updateUserProfile, logout, deleteUser } from '@/services/user'
import { checkEmail, registerUser, loginWithEmail, registerWithEmail } from '@/services/emailAuth'

export default createStore({
  state: {
    user: JSON.parse(localStorage.getItem('user_data')) || {
      name: 'Guest User',
      email: 'guest@example.com',
      picture: 'https://via.placeholder.com/40',
      level: 1,
      points: 0,
      albums: []
    },
    isAuthenticated: !!localStorage.getItem('auth_token'), // Check if token exists
    loading: false,
    error: null
  },
  getters: {
    getUser: state => state.user,
    isAuthenticated: state => state.isAuthenticated,
    isLoading: state => state.loading,
    getError: state => state.error
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      state.isAuthenticated = true
      // Persist user data in localStorage
      localStorage.setItem('user_data', JSON.stringify(user))
    },
    CLEAR_USER(state) {
      state.user = {
        name: 'Guest User',
        email: 'guest@example.com',
        picture: 'https://via.placeholder.com/40',
        level: 1,
        points: 0,
        albums: []
      }
      state.isAuthenticated = false

      // Clear the token and user data from localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')

      // Clear any cookies
      document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    UPDATE_USER(state, userData) {
      state.user = { ...state.user, ...userData }
      // Persist updated user data in localStorage
      localStorage.setItem('user_data', JSON.stringify(state.user))
    }
  },
  actions: {
    async fetchUser({ commit }) {
      commit('SET_LOADING', true)
      try {
        const userData = await fetchUserData()
        commit('SET_USER', userData)
        commit('SET_ERROR', null)
      } catch (error) {
        // Clear user data on authentication failure
        commit('CLEAR_USER')
        commit('SET_ERROR', error.message || 'Failed to fetch user data')

        // If it's an IP mismatch or authentication error, redirect to login
        if (error.message && (error.message.includes('IP') || error.message.includes('Authentication failed'))) {
          // Force redirect to login
          window.location.href = '/login'
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteAccount({ commit }) {
      commit('SET_LOADING', true)
      try {
        // Call the delete user API endpoint
        await deleteUser()

        // Clear user data
        commit('CLEAR_USER')
        commit('SET_ERROR', null)
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to delete account')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async loginWithGoogle({ commit }, idToken) {
      commit('SET_LOADING', true)
      try {
        const response = await authenticateWithGoogle(idToken)
        // The auth service returns user data directly
        if (response && response.user) {
          // Set the user data from the response
          commit('SET_USER', {
            ...response.user,
            level: response.user.level || 1,
            points: response.user.points || 0,
            albums: response.user.albums || []
          })
          commit('SET_ERROR', null)
          return response
        } else if (response && response.error) {
          // Handle error from auth service
          throw new Error(response.error)
        } else {
          throw new Error('Authentication failed - no user data received')
        }
      } catch (error) {
        // Handle axios error
        let errorMessage = error.message || 'Failed to authenticate with Google'

        // Check if it's an axios error with response data
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error
        }

        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async loginWithSpotify({ commit }, { code, redirectUri }) {
      commit('SET_LOADING', true)
      try {
        const response = await authenticateWithSpotify(code, redirectUri)
        // The auth service returns user data directly
        if (response && response.user) {
          // Set the user data from the response
          commit('SET_USER', {
            ...response.user,
            level: response.user.level || 1,
            points: response.user.points || 0,
            albums: response.user.albums || []
          })
          commit('SET_ERROR', null)
          return response
        } else if (response && response.error) {
          // Handle error from auth service
          throw new Error(response.error)
        } else {
          throw new Error('Authentication failed - no user data received')
        }
      } catch (error) {
        // Handle axios error
        let errorMessage = error.message || 'Failed to authenticate with Spotify'

        // Check if it's an axios error with response data
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error
        }

        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async logout({ commit }) {
      commit('SET_LOADING', true)
      try {
        await logout()
        commit('CLEAR_USER')
        commit('SET_ERROR', null)
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to logout')
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateUserProfile({ commit }, userData) {
      commit('SET_LOADING', true)
      try {
        const updatedUser = await updateUserProfile(userData)
        commit('UPDATE_USER', updatedUser)
        commit('SET_ERROR', null)
        return updatedUser
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to update profile')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Email Authentication Actions
    async checkEmailForAuth({ commit }, payload) {
      commit('SET_LOADING', true)
      try {
        const email = typeof payload === 'string' ? payload : payload.email
        const recaptchaVerificationId = typeof payload === 'object' ? payload.recaptchaVerificationId : null

        const response = await checkEmail(email, recaptchaVerificationId)
        commit('SET_ERROR', null)
        return response
      } catch (error) {
        const errorMessage = error.message || 'Failed to check email'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async registerUserWithName({ commit }, { email, name }) {
      commit('SET_LOADING', true)
      try {
        const response = await registerUser(email, name)
        commit('SET_ERROR', null)
        return response
      } catch (error) {
        const errorMessage = error.message || 'Failed to register user'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async loginWithEmailAuth({ commit }, { email, otp, password }) {
      commit('SET_LOADING', true)
      try {
        const response = await loginWithEmail(email, otp, password)

        if (response && response.user) {
          commit('SET_USER', {
            ...response.user,
            level: response.user.level || 1,
            points: response.user.points || 0,
            albums: response.user.albums || []
          })
          commit('SET_ERROR', null)
          return response
        } else {
          throw new Error('Authentication failed - no user data received')
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to login with email'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async registerWithEmailAuth({ commit }, { email, name, otp, password }) {
      commit('SET_LOADING', true)
      try {
        const response = await registerWithEmail(email, name, otp, password)

        if (response && response.user) {
          commit('SET_USER', {
            ...response.user,
            level: response.user.level || 1,
            points: response.user.points || 0,
            albums: response.user.albums || []
          })
          commit('SET_ERROR', null)
          return response
        } else {
          throw new Error('Registration failed - no user data received')
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to register with email'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  modules: {
    // Add modules here if needed
  }
})
